import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnChanges,
  output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ContentViewComponent } from '@principle-theorem/ng-interactions';
import { InformationBoxComponent } from '@principle-theorem/ng-principle-shared';
import {
  NgCommonModule,
  SelectionListStore,
} from '@principle-theorem/ng-shared';
import {
  IClinicalNote,
  IReferralClinicalNote,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  HISTORY_DATE_FORMAT,
  isSameIdentifier,
  isSameRef,
  WithRef,
} from '@principle-theorem/shared';

@Component({
  selector: 'pr-referral-clinical-note-selection',
  imports: [
    NgCommonModule,
    MatSlideToggleModule,
    MatTableModule,
    MatCheckboxModule,
    MatIconModule,
    MatTooltipModule,
    ContentViewComponent,
    InformationBoxComponent,
  ],
  providers: [SelectionListStore],
  templateUrl: './referral-clinical-note-selection.component.html',
  styleUrl: './referral-clinical-note-selection.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReferralClinicalNoteSelectionComponent implements OnChanges {
  selectionList = inject(SelectionListStore<WithRef<IClinicalNote>>);

  clinicalNotes = input<WithRef<IClinicalNote>[]>([]);
  referralClinicalNotes = input<WithRef<IReferralClinicalNote>[]>([]);
  selectedRefs = input<DocumentReference<IClinicalNote>[]>([]);
  toggleEnabled = input<boolean>(false);

  selectionChange = output<DocumentReference<IClinicalNote>[]>();
  toggleChange = output<boolean>();

  readonly dateFormat = HISTORY_DATE_FORMAT;
  readonly columnsToDisplay = ['recordDate', 'recordedBy', 'expand', 'select'];

  expandedRecordIds = signal<Set<string>>(new Set<string>());
  allExpanded = computed(() => {
    const notes = this.clinicalNotes();
    const expandedIds = this.expandedRecordIds();
    return (
      notes.length > 0 && notes.every((note) => expandedIds.has(note.ref.id))
    );
  });
  selected = toSignal(this.selectionList.selected$, { initialValue: [] });

  isRecordExpanded = computed(() => {
    const expandedIds = this.expandedRecordIds();
    return (record: WithRef<IClinicalNote>) => expandedIds.has(record.ref.id);
  });

  constructor() {
    this.selectionList.setCompareFn(isSameRef);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['clinicalNotes']) {
      const notes = this.clinicalNotes();
      this.selectionList.loadOptions(notes);
      this._restoreSelection(notes);
      return;
    }

    if (changes['selectedRefs']) {
      this._restoreSelection(this.clinicalNotes());
    }
  }

  toggle(record: WithRef<IClinicalNote>): void {
    this.expandedRecordIds.update((ids) => {
      const newIds = new Set(ids);
      if (newIds.has(record.ref.id)) {
        newIds.delete(record.ref.id);
        return newIds;
      }
      newIds.add(record.ref.id);
      return newIds;
    });
  }

  toggleAllExpanded(): void {
    if (this.allExpanded()) {
      this.expandedRecordIds.set(new Set());
      return;
    }
    const allIds = new Set(this.clinicalNotes().map((r) => r.ref.id));
    this.expandedRecordIds.set(allIds);
  }

  onToggleChange(enabled: boolean): void {
    this.toggleChange.emit(enabled);
    if (enabled && this.selectedRefs().length === 0) {
      this._preselectReferralNotes();
    }
  }

  onNoteToggle(note: WithRef<IClinicalNote>): void {
    this.selectionList.toggleSelected(note);
    this._emitSelection();
  }

  onToggleAll(): void {
    this.selectionList.toggleAllSelected();
    this._emitSelection();
  }

  private _preselectReferralNotes(): void {
    const referralNotes = this.referralClinicalNotes();
    const patientNotes = this.clinicalNotes();

    if (referralNotes.length === 0) {
      return;
    }

    this.selectionList.resetSelected();
    patientNotes.forEach((patientNote) => {
      if (
        referralNotes.some((refNote) =>
          isSameIdentifier(refNote.clinicalNote, patientNote)
        )
      ) {
        this.selectionList.setSelected(patientNote, true);
      }
    });
    this._emitSelection();
  }

  private _emitSelection(): void {
    const refs = this.selected().map((note) => note.ref);
    this.selectionChange.emit(refs);
  }

  private _restoreSelection(notes: WithRef<IClinicalNote>[]): void {
    const refs = this.selectedRefs();
    if (refs.length === 0) {
      this.selectionList.resetSelected();
      return;
    }
    this.selectionList.resetSelected();
    notes.forEach((note) => {
      if (refs.some((ref) => isSameRef(ref, note.ref))) {
        this.selectionList.setSelected(note, true);
      }
    });
  }
}
